import React, { useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';

import {
  MainContainer,
  ContentContainer,
  PageTitle,
} from 'app/src/elements';
import DownloadCsvButton from 'app/src/elements/DownloadCsvButton';
import HorizontalScroll from 'app/src/elements/HorizontalScroll';
import useCustomizableColumns from
  'app/src/elements/CustomizableColumns/useCustomizableColumns';
import { Link, useParams } from 'app/src/utils/routing';
import { currency } from 'app/src/utils/stringHelpers';

import columns from './columns';
import IndexFilters, {
  INCLUDE_VOID_FILTER,
  SCOPE_NOT_VOID,
  SCOPE_POSTING,
} from './IndexFilters';
import { TXN_LINES_WITH_COUNT, TXN_LINE_TOTALS } from './queries';

// Size the table to its content so wide column sets scroll instead of squeezing.
const TABLE_STYLES = { table: { width: 'max-content', minWidth: '100%' } };

const SEARCH_FILTER = 'search';

const REGISTER_TABLE_KEY = 'accounting.register';

// Unfiltered, the register covers every posting line ever recorded. Cap the
// export and say so, rather than hanging the browser or truncating in silence.
export const CSV_ROW_CAP = 25000;

const TxnLines = () => {
  const [refetch, setRefetch] = useState();
  const [resource, setResource] = useState();
  const [filters, setFilters] = useState({});

  const { type: resourceType, id: resourceId } = useParams();
  const isScoped = !!resourceType;

  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    // A resource register is one account's ledger: hiding voided or
    // non-posting lines there would break reconciliation against it.
    scopes: isScoped ? [] : [SCOPE_POSTING, SCOPE_NOT_VOID],
  };

  // Set only when scoped. Sending `resourceType: undefined` would still count
  // as a present key, which Apollo's cache and MockedProvider both compare on.
  if (isScoped) {
    queryVariables.resourceType = resourceType;
    queryVariables.resourceId = resourceId;
  }

  if (filters[INCLUDE_VOID_FILTER]) {
    queryVariables.scopes =
      queryVariables.scopes.filter(scope => SCOPE_NOT_VOID !== scope);
  }

  const setFilter = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (!value || INCLUDE_VOID_FILTER === key) { return; }
    if (Array.isArray(value) && 0 === value.length) { return; }

    if (SEARCH_FILTER === key) {
      queryVariables.search = value;
    } else {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const client = useApolloClient();
  // A ref, not state: preflight-tables reports settings from its own render,
  // and only the download reads them.
  const tableSettings = useRef();
  const [truncatedAt, setTruncatedAt] = useState();

  const fetchAllEntries = async () => {
    const [sort] = tableSettings.current?.order || [];

    const { data } = await client.query({
      query: TXN_LINES_WITH_COUNT,
      fetchPolicy: 'no-cache',
      variables: {
        ...queryVariables,
        limit: CSV_ROW_CAP,
        offset: 0,
        sort: sort?.key,
        sortDirection: sort?.direction,
      },
    });

    const { count, entries } = data.txnLinesWithCount;

    setTruncatedAt(count > CSV_ROW_CAP ? count : null);

    return entries;
  };

  const { data: totalsData, loading: totalsLoading } = useQuery(
    TXN_LINE_TOTALS, { variables: queryVariables }
  );

  const totals = totalsData?.txnLineTotals;

  const { columns: tableColumns, picker, exportColumns } =
    useCustomizableColumns({
      tableKey: REGISTER_TABLE_KEY,
      columns,
    });

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>
          Register
          {resource && (
            <Link to={resource.path}>{' - '}{resource.name}</Link>
          )}
        </PageTitle>

        <IndexFilters
          filters={filters}
          setFilter={setFilter}
          onClear={() => setFilters({})}
        />

        <div style={{ padding: '8px 0', fontWeight: 'bold' }}>
          {totalsLoading || !totals ?
            'Loading totals…' :
            [
              `${totals.count.toLocaleString()} lines`,
              `Debit ${currency(totals.debitLessCredit)}`,
              `Credit ${currency(totals.creditLessDebit)}`,
            ].join(' · ')}
        </div>

        <HorizontalScroll pinnedControls>
          <DataTable.Gql
            setRefetch={setRefetch}
            query={TXN_LINES_WITH_COUNT}
            queryName='txnLinesWithCount'
            queryVariables={queryVariables}
            dataUpdated={data => setResource(data?.resource)}
            setSettings={settings => { tableSettings.current = settings; }}
            columns={tableColumns}
            buttons={
              <>
                {picker}
                <DownloadCsvButton
                  columns={exportColumns}
                  fetchEntries={fetchAllEntries}
                  filePrefix="register"
                />
                {truncatedAt && (
                  <span style={{ paddingLeft: 8 }}>
                    {`Showing first ${CSV_ROW_CAP.toLocaleString()} of ` +
                      `${truncatedAt.toLocaleString()} — narrow your filters ` +
                      'to export the rest.'}
                  </span>
                )}
              </>
            }
            pageLength={25}
            lengthMenu={ [10, 25, 50, 100, 200, 500] }
            styles={TABLE_STYLES}
            disableSearch
            htmlTable
          />
        </HorizontalScroll>
      </ContentContainer>
    </MainContainer>
  );
};

export default TxnLines;
